/**
 * Environment Toggle Component
 * Allows switching between production and test environments
 */

import React from 'react';
import { useAppStore } from '@/store/useAppStore';
import { useAuth } from '@/components/auth/AuthProvider';

interface EnvironmentToggleProps {
  className?: string;
}

export function EnvironmentToggle({ className = '' }: EnvironmentToggleProps): React.ReactElement {
  const activeEnvironment = useAppStore((state) => state.activeEnvironment);
  const toggleEnvironment = useAppStore((state) => state.toggleEnvironment);
  const { getAccessToken } = useAuth();
  const isTest = activeEnvironment === 'test';

  const handleToggle = (): void => {
    // Toggle the environment first
    toggleEnvironment();

    // Then fetch data with the new environment
    // Small delay to ensure state is updated before fetching
    setTimeout(() => {
      void (async () => {
        try {
          const token = await getAccessToken();
          if (token) {
            // Get fetchDataFromBackend from current store state to ensure fresh headers
            const { fetchDataFromBackend } = useAppStore.getState();
            await fetchDataFromBackend(token);
          }
        } catch (error) {
          console.warn('[EnvironmentToggle] Failed to fetch data after toggle:', error);
        }
      })();
    }, 50);
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`
        relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
        transition-all duration-200
        ${isTest
          ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 ring-2 ring-amber-400'
          : 'bg-charcoal-100 text-charcoal-600 hover:bg-charcoal-200'
        }
        ${className}
      `}
      title={isTest ? 'Test-Umgebung aktiv - Klicken für Produktion' : 'Produktions-Umgebung aktiv - Klicken für Test'}
    >
      {/* Toggle indicator */}
      <span
        className={`
          w-2 h-2 rounded-full transition-colors duration-200
          ${isTest ? 'bg-amber-500 animate-pulse' : 'bg-green-500'}
        `}
      />

      {/* Label */}
      <span className="uppercase tracking-wide">
        {isTest ? 'Test' : 'Prod'}
      </span>

      {/* Switch icon */}
      <svg
        className="w-3 h-3 opacity-50"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
    </button>
  );
}

/**
 * Environment Badge - A simpler indicator without toggle functionality
 */
export function EnvironmentBadge({ className = '' }: { className?: string }): React.ReactElement | null {
  const { activeEnvironment } = useAppStore();
  const isTest = activeEnvironment === 'test';

  if (!isTest) return null; // Only show badge in test mode

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-bold
        bg-amber-500 text-white animate-pulse
        ${className}
      `}
    >
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      TEST
    </div>
  );
}
